import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { alpha, keyframes, useTheme } from '@mui/material';
import { LiveTranscriptLine } from '../hooks/useLiveTranscription';
import { TranscriptSegment } from '../utils/backend-utils';

interface InterrogationTranscriptViewProps {
  persistedSegments?: TranscriptSegment[];
  liveLines: LiveTranscriptLine[];
  partialText: string;
  partialSpeakerLabel?: string;
  speakerRegistry?: Record<string, string>;
  isRecording?: boolean;
  isConnecting?: boolean;
  isReconnecting?: boolean;
  awaitingStart?: boolean;
}

export interface PlaybookLine {
  id: string;
  speakerLabel: string;
  text: string;
  isPartial?: boolean;
}

const typingDot = keyframes`
  0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-2px); }
`;

function TypingDots() {
  return (
    <Box component="span" sx={{ display: 'inline-flex', gap: 0.35, ml: 0.5, verticalAlign: 'middle' }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          component="span"
          sx={{
            width: 4,
            height: 4,
            borderRadius: '50%',
            bgcolor: 'text.secondary',
            display: 'inline-block',
            animation: `${typingDot} 1.2s infinite ease-in-out`,
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </Box>
  );
}

export function displaySpeakerName(
  speakerLabel: string | undefined,
  registry: Record<string, string>,
): string {
  if (speakerLabel && registry[speakerLabel]) {
    return registry[speakerLabel];
  }
  if (speakerLabel && speakerLabel !== 'unknown') {
    const match = /^Speaker[_\s-]?(\d+)$/i.exec(speakerLabel);
    if (match) {
      const index = parseInt(match[1], 10);
      if (index >= 1 && index <= 26) {
        return `Person ${String.fromCharCode(64 + index)}`;
      }
    }
    return speakerLabel;
  }
  return 'Person A';
}

export function InterrogationTranscriptView({
  persistedSegments = [],
  liveLines,
  partialText,
  partialSpeakerLabel = 'unknown',
  speakerRegistry = {},
  isRecording = false,
  isConnecting = false,
  isReconnecting = false,
  awaitingStart = false,
}: InterrogationTranscriptViewProps) {
  const theme = useTheme();

  const playbookLines = useMemo(() => {
    const persistedIds = new Set(
      persistedSegments.map((s) => `${s.timestamp}-${s.message}`)
    );
    const unsyncedLive = liveLines.filter(
      (line) => !persistedIds.has(`${line.endedAt}-${line.text}`)
    );

    const fromPersisted: PlaybookLine[] = persistedSegments
      .filter((seg) => Boolean(seg.message?.trim()))
      .map((seg) => ({
        id: `p-${seg.timestamp}-${seg.message}`,
        speakerLabel: seg.speaker_label || 'unknown',
        text: seg.message!.trim(),
      }));

    const fromLive: PlaybookLine[] = unsyncedLive.map((line) => ({
      id: line.id,
      speakerLabel: line.speakerLabel,
      text: line.text,
    }));

    return [...fromPersisted, ...fromLive];
  }, [liveLines, persistedSegments]);

  const showEmptyHint = playbookLines.length === 0 && !partialText && (isRecording || isConnecting || isReconnecting || awaitingStart);

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: '12px',
        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          Transcript
        </Typography>
        {isConnecting && (
          <Chip size="small" label="Connecting…" variant="outlined" />
        )}
        {isReconnecting && !isConnecting && (
          <Chip size="small" label="Reconnecting…" color="warning" variant="outlined" />
        )}
        {isRecording && !isConnecting && !isReconnecting && (
          <Chip
            size="small"
            color="error"
            icon={<FiberManualRecordIcon sx={{ fontSize: '12px !important' }} />}
            label="Recording"
            sx={{
              '& .MuiChip-icon': { color: 'error.main' },
              animation: 'pulse 1.8s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.65 },
              },
            }}
          />
        )}
      </Box>

      <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25, minHeight: 72 }}>
        {showEmptyHint && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {isConnecting
              ? 'Setting up live transcription…'
              : isReconnecting
                ? 'Connection dropped — reconnecting automatically…'
              : isRecording
                ? 'Recording — speak into your microphone. Lines appear here as people talk.'
                : 'Press Start recording below to capture the meeting.'}
          </Typography>
        )}

        {playbookLines.map((line) => (
          <Typography key={line.id} variant="body2" sx={{ lineHeight: 1.55 }}>
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {displaySpeakerName(line.speakerLabel, speakerRegistry)}:
            </Box>{' '}
            {line.text}
          </Typography>
        ))}

        {partialText && (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {displaySpeakerName(partialSpeakerLabel, speakerRegistry)}:
            </Box>{' '}
            {partialText}
            <TypingDots />
          </Typography>
        )}
      </Box>
    </Box>
  );
}
